import { ChevronLeftIcon } from '@heroicons/react/16/solid'
import {
  type ActionFunctionArgs,
  type LoaderFunctionArgs,
  redirect,
} from '@remix-run/node'
import {
  Form,
  Link,
  json,
  useActionData,
  useLoaderData,
  useLocation,
  useNavigate,
} from '@remix-run/react'
import Slider from 'rc-slider'
import { type ZodError, z } from 'zod'
import { zx } from 'zodix'
import { fetchReportById } from '~/models/report.v1.server'
import { commitSession, getSession } from '~/models/sessions.server'
import { client } from '~/services/Zendesk'

import 'rc-slider/assets/index.css'
import { useState } from 'react'

export async function loader({ params }: LoaderFunctionArgs) {
  const [reportResult] = await Promise.allSettled([fetchReportById(params.id!)])

  if (reportResult.status === 'rejected') {
    throw new Response('Error fetching report', { status: 500 })
  }

  const zendeskId = reportResult.value.quote?.zendeskTicket

  const customerSatisfaction = [
    'Very Satisfied',
    'Satisfied',
    'Neutral',
    'Dissatisfied',
    'Very Dissatisfied',
  ]
  const questionsLikely = ['Likely', 'Unsure', 'Not likely']

  return {
    zendeskId,
    questionsLikely,
    customerSatisfaction,
  }
}

const schema = z.object({
  reason: z.string({
    message: 'Please select a reason',
  }),
  likely: z.string().min(1, {
    message:
      'Please select how likely you are to return to complete your purchase in the future?',
  }),
  comments: z.string().optional(),
  zendeskId: zx.NumAsString,
})

function errorAtPath(error: ZodError, path: string) {
  return error.issues.find((issue) => issue.path[0] === path)?.message
}

export async function action({ params, request }: ActionFunctionArgs) {
  const session = await getSession(request.headers.get('Cookie'))
  const url = new URL(request.url)
  const parentPath = url.pathname.replace('form', '')
  const surveyParsedResponse = await zx.parseFormSafe(request, schema)

  const survey = surveyParsedResponse.data

  if (surveyParsedResponse.success && survey) {
    const reasonHtml = Array.isArray(survey.reason)
      ? survey.reason.map((reason) => `${reason}`).join('</li><li>')
      : `${survey.reason}`

    try {
      let zendeskId: number

      if (process.env.APP_ENV === 'production') {
        zendeskId = survey.zendeskId
      } else {
        zendeskId = 3436
      }

      // Add a comment to the ticket in Zendesk
      await client.tickets.update(zendeskId, {
        ticket: {
          subject: 'Feedback Survey Form',
          tags: process.env.APP_ENV === 'production' ? [] : ['test'],
          status: 'pending',
          comment: {
            public: false,
            html_body: `<p><strong>Feedback Survey Form</strong></p><p><i>How satisfied were you with our service?</i> ${survey.reason}</p><br /><p><i>How likely are you to use us again in the future?</i> ${survey.likely}</p><p><i>Comment:</i> ${survey.comments}</p>`,
          },
        },
      })

      // Redirect to the user page
      session.set('feedbackGiven', true)
      return redirect(parentPath, {
        headers: { 'Set-Cookie': await commitSession(session) },
      })
    } catch (error) {
      if (process.env.APP_ENV === 'production') {
        console.error(
          JSON.stringify({
            severity: 'ERROR',
            message: 'Error creating ticket in Zendesk',
            error,
            proposal: params.id,
            ...survey,
          }),
        )
      } else {
        console.error(error)
      }
    }
  } else {
    console.error(surveyParsedResponse.error)

    // Get the error messages and return them to the client.
    return json({
      success: false,
      reasonError: errorAtPath(surveyParsedResponse.error, 'reason'),
      likelyError: errorAtPath(surveyParsedResponse.error, 'likely'),
      commentsError: errorAtPath(surveyParsedResponse.error, 'comments'),
      zendeskIdError: errorAtPath(surveyParsedResponse.error, 'zendeskId'),
    })
  }
}

export default function Survey() {
  const location = useLocation()
  const back = location.pathname.replace('/form', '')

  const [reason, setReason] = useState<number>(1)
  const { zendeskId, questionsLikely, customerSatisfaction } =
    useLoaderData<typeof loader>()
  const data = useActionData<typeof action>()

  const getMarkFromValue = (value: number) => {
    return Math.ceil(value / 10)
  }

  return (
    <section>
      <Link
        to={back}
        className="font-semibold flex gap-1 text-sm no-underline mb-8"
      >
        <ChevronLeftIcon className="w-6 inline" /> Back to overview
      </Link>
      <h3>
        We'd love to hear your thoughts to improve our Concierge Experience
      </h3>

      <Form method="post">
        <p className="block mt-4 font-bold">
          How satisfied were you with our service?
        </p>
        <Slider
          min={1}
          defaultValue={reason}
          marks={{
            1: 1,
            11: 2,
            22: 3,
            33: 4,
            44: 5,
            55: 6,
            66: 7,
            77: 8,
            88: 9,
            100: 10,
          }}
          step={null}
          trackStyle={{ backgroundColor: '#cc0000' }}
          handleStyle={{
            borderColor: '#cc0000',
          }}
          activeDotStyle={{ borderColor: '#cc0000' }}
          className={`mt-4 mb-12 ${
            data?.likelyError ? 'text-error border border-error' : ''
          }`}
          onChange={(value) => {
            const markValue = getMarkFromValue(
              Array.isArray(value) ? value[0] : value,
            )
            setReason(markValue)
            console.log('Mark value changed to:', markValue)
          }}
        />

        <label htmlFor="likely" className="block mt-4 font-bold">
          How likely are you to use us again in the future?
        </label>

        <select
          id="likely"
          name="likely"
          className={`block w-full mt-1 bg-gray-200 p-2 ${
            data?.likelyError ? 'text-error border border-error' : ''
          }`}
          aria-placeholder="Please select"
        >
          <option value="">Please select</option>
          {questionsLikely.map((question) => (
            <option key={question} value={question}>
              {question}
            </option>
          ))}
        </select>
        {data?.likelyError && (
          <div className="text-error font-bold">{data.likelyError}</div>
        )}
        <label htmlFor="comments" className="block mt-4 font-bold">
          Is there anything specific we could do to make your experience better?
        </label>
        <textarea
          id="comments"
          name="comments"
          className="block w-full mt-1 bg-gray-200 p-2"
          rows={5}
        />
        <input type="hidden" name="reason" value={reason.toString()} />
        <input type="hidden" name="zendeskId" value={zendeskId} />
        <button
          type="submit"
          className="mt-4 bg-themecolour-primary w-full text-white p-2"
        >
          Submit
        </button>
        <div className="text-xs mt-4">
          <sup>*</sup>Indicates required field
        </div>
      </Form>
    </section>
  )
}
